import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_RECEPTION_ORDER_DIALOG } from '../../../../core/constants/dialog';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useBusinessCore } from '../../../../core/modules/business';
import { useMasterCore } from '../../../../core/modules/master';
import { FilterDrawer, FilterField } from '../../shared/FilterDrawer';

interface ReceptionOrderFilters {
  search?: string;
  business_id?: number;
  start_date?: string;
  end_date?: string;
}

const Header = () => {
  const { user } = useMeCore();
  const { total, getReceptionOrders } = useReceptionCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { getTypes } = useMasterCore();
  const { getBusinesses, businesses } = useBusinessCore();

  const [filters, setFilters] = useState<ReceptionOrderFilters>({
    search: '',
    business_id: undefined,
    start_date: undefined,
    end_date: undefined,
  });

  const canWrite = useMemo(
    () =>
      user && (user.privileges.superAdmin || user.privileges.writeReception),
    [user]
  );

  // Cargar proveedores cuando se monte el componente
  useEffect(() => {
    if (companyId) {
      getBusinesses(companyId);
    }
  }, [companyId, getBusinesses]);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    if (companyId) {
      const apiFilters: any = {};
      if (filters.search) {
        apiFilters.search = filters.search;
      }
      if (filters.business_id) {
        apiFilters.business_id = filters.business_id;
      }
      if (filters.start_date) {
        apiFilters.start_date = filters.start_date;
      }
      if (filters.end_date) {
        apiFilters.end_date = filters.end_date;
      }

      getReceptionOrders(companyId, apiFilters);
    }
  }, [companyId, filters.search, filters.business_id, filters.start_date, filters.end_date, getReceptionOrders]);

  const openCreateDialog = useCallback(() => {
    if (companyId) {
      getTypes(companyId, 'acquisition');
      getBusinesses(companyId);
      openDialog(CREATE_RECEPTION_ORDER_DIALOG);
    }
  }, [companyId, getBusinesses, getTypes, openDialog]);

  const handleFiltersChange = useCallback((newFilters: ReceptionOrderFilters) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters?: ReceptionOrderFilters) => {
    if (companyId && appliedFilters) {
      const apiFilters: any = {};
      if (appliedFilters.search) {
        apiFilters.search = appliedFilters.search;
      }
      if (appliedFilters.business_id) {
        apiFilters.business_id = appliedFilters.business_id;
      }
      if (appliedFilters.start_date) {
        apiFilters.start_date = appliedFilters.start_date;
      }
      if (appliedFilters.end_date) {
        apiFilters.end_date = appliedFilters.end_date;
      }

      getReceptionOrders(companyId, apiFilters);
    }
  }, [companyId, getReceptionOrders]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: undefined,
      business_id: undefined,
      start_date: undefined,
      end_date: undefined,
    });
    if (companyId) {
      getReceptionOrders(companyId, {});
    }
  }, [companyId, getReceptionOrders]);

  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Buscar órdenes de ingreso...',
    },
    {
      key: 'business_id',
      label: 'Proveedor',
      type: 'select',
      options: businesses.map((business) => ({
        id: business.id,
        name: business.name || '',
        description: business.name,
      })),
    },
    {
      key: 'start_date',
      label: 'Fecha desde',
      type: 'date',
    },
    {
      key: 'end_date',
      label: 'Fecha hasta',
      type: 'date',
    },
  ];

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Ordenes de ingreso
          </Typography>
          <Typography className="text-14 font-medium" color="text.secondary">
            {total} orden/es
          </Typography>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-4 mt-4 sm:mt-0">
          <div className="flex flex-col w-full">
            <div className="flex w-full items-center gap-4">
              <FilterDrawer
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                fields={filterFields}
                disabled={!companyId}
              />

              {canWrite && (
                <Button
                  variant="contained"
                  color="secondary"
                  className="my-auto mx-2"
                  disabled={!companyId}
                  onClick={openCreateDialog}
                  sx={{
                    minWidth: 120,
                    height: 48,
                    fontWeight: 700,
                    fontSize: 16,
                    borderRadius: 999,
                    boxShadow: '0 2px 8px 0 rgba(80,80,200,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AddCircleOutlineOutlinedIcon sx={{ fontSize: 22 }} />
                  <span className="mx-8">Crear</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
